import React, {useEffect, useState} from 'react';
import ReportViewer from './ReportViewer';
import './App.css';

// Define the available reports outside the component to ensure they're always available
const availableReports = [
    {
        id: '2025-01-31',
        date: '2025-01-31',
        title: 'Solar Lab Incident Report - January 31, 2025',
        description: 'Comprehensive analysis of solar lab incident with detailed plots and metrics',
        diagramViewerUrl: '/viewer/2025-01-31/plots/plots-viewer.html',
        reportUrl: '/viewer/2025-01-31/report/report.html',
        diagramCount: 25,
        hasReport: true
    },
    {
        id: '2025-07-28',
        date: '2025-07-28',
        title: 'Solar Lab Incident Report - July 28, 2025',
        description: 'Detailed incident report with system analysis and performance metrics',
        diagramViewerUrl: '/viewer/2025-07-28/plots/plots-viewer.html',
        reportUrl: '/viewer/2025-07-28/report/index.html',
        diagramCount: 25,
        hasReport: true
    }
];

function App() {
    const [reports, setReports] = useState(availableReports); // Initialize with availableReports
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [currentView, setCurrentView] = useState('dashboard'); // dashboard, report, specific-report
    const [currentReport, setCurrentReport] = useState(null);
    const [specificReportDate, setSpecificReportDate] = useState(null);

    useEffect(() => {
        console.log('App useEffect running...');
        
        // Check if we're on a report URL or redirected from 404.html
        let path = window.location.pathname;
        
        // Check if we were redirected from 404.html with a path parameter
        const urlParams = new URLSearchParams(window.location.search);
        const redirectPath = urlParams.get('path');
        if (redirectPath) {
            path = redirectPath;
            // Update the URL without reloading the page
            window.history.replaceState(null, null, redirectPath);
        }
        
        // Handle hash-based routing for production builds
        const hash = window.location.hash;
        if (hash) {
            if (hash.startsWith('#/viewer/')) {
                path = hash.replace('#', '');
            } else if (hash.startsWith('#/specific-report/')) {
                path = hash.replace('#/specific-report/', '/specific-report/');
            }
        }
        
        console.log('Current path:', path);
        
        // Check if we're on a specific report viewer URL (without /report/ or /plots/)
        const viewerMatch = path.match(/\/viewer\/([^\/]+)\/?$/);
        if (viewerMatch) {
            // We're on a specific report's main page - set specific report view mode
            const reportDate = viewerMatch[1];
            console.log('Setting specific report view for:', reportDate);
            
            // Set the view mode to specific-report and store the date
            setCurrentView('specific-report');
            setSpecificReportDate(reportDate);
            setLoading(false);
            return;
        }
        
        if (path.includes('/viewer/') && !path.includes('/report/') && !path.includes('/plots/')) {
            // Extract report date from URL
            const reportMatch = path.match(/\/viewer\/([^\/]+)\/?$/);
            if (reportMatch) {
                const reportDate = reportMatch[1];
                console.log('Setting specific report view for:', reportDate);
                setCurrentView('dashboard');
                setLoading(false);
                return;
            }
        }
        
        // Check if we're on a specific-report URL (from plots viewer navigation)
        if (path.includes('/specific-report/')) {
            // Extract report date from URL
            const reportMatch = path.match(/\/specific-report\/([^\/]+)\/?$/);
            if (reportMatch) {
                const reportDate = reportMatch[1];
                console.log('Setting specific report view for:', reportDate);
                setSpecificReportDate(reportDate);
                setCurrentView('specific-report');
                setLoading(false);
                return;
            }
        }

        if (path.includes('/report/') && path.includes('/viewer/')) {
            // Extract report path from URL
            const reportMatch = path.match(/\/viewer\/([^\/]+)\/report\//);
            if (reportMatch) {
                const reportDate = reportMatch[1];
                const reportPath = `/viewer/${reportDate}/report/report.md`;
                console.log('Setting report view for:', reportDate);
                setCurrentReport({ date: reportDate, path: reportPath });
                setCurrentView('report');
                setLoading(false);
                return;
            }
        }

        // Dashboard is already loaded with availableReports, just stop loading
        console.log('Loading dashboard...');
        console.log('Reports already initialized with availableReports');
        setLoading(false);
    }, []);

    const handleReportClick = (report) => {
        const reportPath = `/viewer/${report.date}/report/report.md`;
        setCurrentReport({ date: report.date, path: reportPath });
        setCurrentView('report');
        // Update URL without page reload
        window.history.pushState({}, '', `/viewer/${report.date}/report/`);
    };

    const handleBackToDashboard = () => {
        setCurrentView('dashboard');
        setCurrentReport(null);
        setSpecificReportDate(null);
        setSearchTerm('');
        window.history.pushState({}, '', '/');
    };

    // Debug logs for search term and reports
    console.log('Current search term:', searchTerm);
    console.log('Available reports:', reports);
    
    // Improved filtering logic
    const filteredReports = searchTerm ? reports.filter(report => {
        // If searchTerm is a date format (like 2025-01-31), do an exact match on the date field
        if (/^\d{4}-\d{2}-\d{2}$/.test(searchTerm)) {
            const match = report.date === searchTerm;
            console.log(`Checking report ${report.id} with date ${report.date} against ${searchTerm}: ${match}`);
            return match;
        }
        // Otherwise do the normal search across all fields
        return report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
               report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
               report.date.includes(searchTerm);
    }) : reports;
    
    console.log('Filtered reports:', filteredReports);

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopState = () => {
            const path = window.location.pathname;
            if (path === '/' || path === '') {
                setCurrentView('dashboard');
                setCurrentReport(null);
                setSpecificReportDate(null);
            } else if (path.includes('/report/') && path.includes('/viewer/')) {
                const reportMatch = path.match(/\/viewer\/([^\/]+)\/report\//);
                if (reportMatch) {
                    const reportDate = reportMatch[1];
                    const reportPath = `/viewer/${reportDate}/report/report.md`;
                    setCurrentReport({ date: reportDate, path: reportPath });
                    setCurrentView('report');
                    setSpecificReportDate(null);
                }
            } else {
                // Check if we're on a specific report viewer URL (without /report/ or /plots/)
                const viewerMatch = path.match(/\/viewer\/([^\/]+)\/?$/);
                if (viewerMatch) {
                    const reportDate = viewerMatch[1];
                    setCurrentView('specific-report');
                    setSpecificReportDate(reportDate);
                    setCurrentReport(null);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    if (loading) {
        return (
            <div className="App">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading reports...</p>
                </div>
            </div>
        );
    }

    // Show report viewer if we're viewing a report
    if (currentView === 'report' && currentReport) {
        return (
            <div className="App">
                <header className="report-header">
                    <button onClick={handleBackToDashboard} className="back-to-dashboard">
                        ← Back to Dashboard
                    </button>
                </header>
                <ReportViewer reportPath={currentReport.path} />
            </div>
        );
    }
    
    // Show specific report view (only showing one report card)
    if (currentView === 'specific-report' && specificReportDate) {
        console.log('Rendering specific report view for:', specificReportDate);
        console.log('Available reports:', reports);
        
        // Find the specific report
        const specificReport = reports.find(report => report.date === specificReportDate);
        console.log('Found specific report:', specificReport);
        
        return (
            <div className="App">
                <header className="App-header">
                    <h1>Solar Lab Dashboard</h1>
                    <p className="subtitle">Viewing specific report: {specificReportDate}</p>
                </header>

                <main className="dashboard">
                    <div className="dashboard-header">
                        <div className="stats">
                            <div className="stat-item">
                                <span className="stat-number">1</span>
                                <span className="stat-label">Selected Report</span>
                            </div>
                        </div>

                        <button onClick={handleBackToDashboard} className="back-to-dashboard">
                            ← View All Reports
                        </button>
                    </div>

                    <div className="reports-grid" style={{ display: 'block' }}>
                        {specificReport ? (
                            <div key={specificReport.id} className="report-card" style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
                                <div className="report-header">
                                    <div className="report-title-text" title={specificReport.title}>{specificReport.title}</div>
                                    <span className="report-date">{specificReport.date}</span>
                                </div>

                                <p className="report-description">{specificReport.description}</p>

                                <div className="report-stats">
                                    <span className="diagram-count">
                                        📊 {specificReport.diagramCount} plots available
                                    </span>
                                </div>

                                <div className="report-actions">
                                    <a
                                        href={specificReport.diagramViewerUrl}
                                        className="action-btn primary"
                                    >
                                        View Plots
                                    </a>
                                    {specificReport.hasReport && (
                                        <button
                                            onClick={() => handleReportClick(specificReport)}
                                            className="action-btn secondary"
                                        >
                                            View Report
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="no-results">
                                <p>Report not found: "{specificReportDate}"</p>
                                <button
                                    onClick={handleBackToDashboard}
                                    className="clear-search-btn"
                                >
                                    View all reports
                                </button>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="App">
            <header className="App-header">
                <h1>Solar Lab Dashboard</h1>
                <p className="subtitle">Comprehensive incident analysis with plots and reports</p>
            </header>

            <main className="dashboard">
                <div className="dashboard-header">
                    <div className="stats">
                        <div className="stat-item">
                            <span className="stat-number">{reports.length}</span>
                            <span className="stat-label">Total Reports</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">{reports.reduce((total, report) => total + report.diagramCount, 0)}</span>
                            <span className="stat-label">Total Plots</span>
                        </div>
                    </div>

                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search reports by title, description, or date..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="clear-search"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                <div className="reports-grid">
                    {filteredReports.length === 0 ? (
                        <div className="no-results">
                            <p>No reports found matching "{searchTerm}"</p>
                            <button
                                onClick={() => setSearchTerm('')}
                                className="clear-search-btn"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        filteredReports.map(report => (
                            <div key={report.id} className="report-card">
                                <div className="report-header">
                                    <div className="report-title-text">{report.title}</div>
                                    <span className="report-date">{report.date}</span>
                                </div>

                                <p className="report-description">{report.description}</p>

                                <div className="report-stats">
                  <span className="diagram-count">
                    📊 {report.diagramCount} plots available
                  </span>
                                </div>

                                <div className="report-actions">
                                    <a
                                        href={report.diagramViewerUrl}
                                        className="action-btn primary"
                                    >
                                        View Plots
                                    </a>
                                    {report.hasReport && (
                                        <button
                                            onClick={() => handleReportClick(report)}
                                            className="action-btn secondary"
                                        >
                                            View Report
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </main>
        </div>
    );
}

export default App;
